import { CostCenter, PURCHASE_ORDER_STATUSES_UPDATE_FORBIDDEN, RightEnum } from "@finway-group/shared/lib/models"
import { CreditCardLink } from "Components/creditCard/CreditCardLink"
import DeletedTooltip from "Components/deletedTooltip"
import EmployeeLink from "Components/employeeLink"
import { WorkflowField } from "Features/pages/expenses/expenseDetails/expenseSourceSection/workflow.field"
import React from "react"
import { useTranslation } from "react-i18next"
import { generatePath, Link } from "react-router-dom"
import { useCostCenterById } from "Shared/hooks/costCenter.hooks"
import { useCostCenter2ById } from "Shared/hooks/costCenter2.hooks"
import { useCardById } from "Shared/hooks/creditCard.hooks"
import { useExpenseAccountById } from "Shared/hooks/expenseAccount.hooks"
import { useEmployee, useLoggedInEmployeeProfile } from "Shared/queries/employee.queries"
import { RoutePathEnum } from "Shared/router/RoutePath.enum"
import { AuthzService } from "Shared/services"
import { getNextApproverId } from "Shared/utils/approvalProcess.utils"
import { formatOptionalDate } from "Shared/utils/date.utils"
import { formatAddress } from "Shared/utils/form.utils"
import { doAllElementsHaveTheSameFieldValue } from "Shared/utils/helper.utils"
import { PurchaseOrder } from "../types/PurchaseOrder.interfaces"
import { isPurchaseOrderApproverVisible } from "../types/utils/helper.util"
import { PurchaseOrderStatusTag } from "./PurchaseOrderStatusTag"
import { PurchaseOrderVendorField } from "./PurchaseOrderVendorField"

interface PurchaseOrderTabContentProps {
    purchaseOrder: PurchaseOrder
}

export const PurchaseOrderTabContent = ({ purchaseOrder }: PurchaseOrderTabContentProps) => {
    const { t } = useTranslation()
    const loggedInUser = useLoggedInEmployeeProfile()

    const nextApproverId = getNextApproverId(purchaseOrder.approvalProcesses)
    const { data: nextApprover } = useEmployee(nextApproverId)
    const { data: requestedBy } = useEmployee(purchaseOrder.requestedBy._id)
    const costCenterLineItem = useCostCenterById(purchaseOrder.lineItems[0].costCenter)
    const costCenter2LineItem = useCostCenter2ById(purchaseOrder.lineItems[0].costCenter2)
    const expenseAccountLineItem = useExpenseAccountById(purchaseOrder.lineItems[0].expenseAccount)

    const { card } = useCardById(purchaseOrder.cardId)

    // Helper function to check if cost center is splitted (similar to invoice logic)
    const isCostCenterUniform = doAllElementsHaveTheSameFieldValue(purchaseOrder.lineItems, "costCenter")
    const isCostCenter2Uniform = doAllElementsHaveTheSameFieldValue(purchaseOrder.lineItems, "costCenter2")
    const isExpenseAccountUniform = doAllElementsHaveTheSameFieldValue(purchaseOrder.lineItems, "expenseAccount")

    const isCostCenterApprover = AuthzService.isRightGrantedForLoggedInUser(RightEnum.CC1__ALL__OWNER) && loggedInUser!.id === costCenterLineItem?.responsibleUser
    const canReadCostCenter = AuthzService.isRightGrantedForLoggedInUser(RightEnum.CC1__ALL__READ) || isCostCenterApprover

    const showApprover = isPurchaseOrderApproverVisible(purchaseOrder)

    const getCostCenterLabel = (costCenter: CostCenter) => (
        <>
            {canReadCostCenter ? <Link to={generatePath(RoutePathEnum.COST_CENTER_DETAIL_PAGE, { id: costCenter._id })}>{costCenter?.name}</Link> : <span>{costCenter?.name}</span>}
            {costCenter?.deleted && <DeletedTooltip translationKey="tooltips:cost_center.was_deleted" deletionDate={costCenter?.dateDeleted} />}
        </>
    )

    return (
        <div className="p-6">
            <ul className="description-list pb-10 overflow-hidden">
                {/* Status */}
                <li className="flex justify-between items-start">
                    <span className="title">{t("label:status")}</span>
                    <span className="text">
                        <PurchaseOrderStatusTag status={purchaseOrder.state.currentState} className="ml-auto" />
                    </span>
                </li>

                {/* Performance date or Expected delivery date */}
                <li className="flex justify-between items-start">
                    <span className="title">{purchaseOrder.isService ? t("purchaseOrders:drawer.expectedServiceDate") : t("purchaseOrders:drawer.expectedDeliveryDate")}</span>
                    <span className="text">{formatOptionalDate(purchaseOrder.expectedFullfilmentDate, "")}</span>
                </li>

                <li className="flex justify-between items-start">
                    <span className="title">{t("label:performance_period_start_date")}</span>
                    <span className="text">{formatOptionalDate(purchaseOrder.performanceDateFrom, "")}</span>
                </li>

                <li className="flex justify-between items-start">
                    <span className="title">{t("label:performance_period_end_date")}</span>
                    <span className="text">{formatOptionalDate(purchaseOrder.performanceDateTo, "")}</span>
                </li>

                {/* Payment terms - Note: This field doesn't exist in the current interface */}
                <li className="flex justify-between items-start">
                    <span className="title">{t("label:payment_terms")}</span>
                    <span className="truncate max-w-100">{purchaseOrder.paymentTerms || ""}</span>
                </li>

                {/* Delivery location - Note: This field doesn't exist in the current interface */}
                <li className="flex justify-between items-start">
                    <span className="title">{t("label:delivery_address")}</span>
                    <span className="text">{formatAddress(purchaseOrder.deliveryLocation)}</span>
                </li>

                {/* Requested by */}
                <li className="flex justify-between items-start">
                    <span className="title">{t("label:requested_by")}</span>
                    <span className="text">{requestedBy ? <EmployeeLink employee={requestedBy} expenseExtendedRefUser={purchaseOrder.requestedBy} /> : <></>}</span>
                </li>

                {/* Next approver */}
                {nextApprover && showApprover ? (
                    <li className="flex justify-between items-start">
                        <span className="title">{t("label:next_approver")}</span>
                        <span className="text">
                            <EmployeeLink employee={nextApprover} allowReplacementIcon />
                        </span>
                    </li>
                ) : null}

                <WorkflowField approvalProcesses={purchaseOrder.approvalProcesses} />

                {/* Cost center 1 - with splitted logic */}
                <li className="flex justify-between items-start">
                    <span className="title">{t("label:cost_center")}</span>
                    <span className="text flex gap-4 items-center">{isCostCenterUniform ? getCostCenterLabel(costCenterLineItem) : t("label:splitted")}</span>
                </li>

                {/* Cost center 2 - with splitted logic */}
                {costCenter2LineItem ? (
                    <li className="flex justify-between items-start">
                        <span className="title">{t("label:cost_center_2")}</span>
                        <span className="text">{isCostCenter2Uniform ? costCenter2LineItem?.name : t("label:splitted")}</span>
                    </li>
                ) : null}

                {/* Expense account - with splitted logic */}
                {expenseAccountLineItem ? (
                    <li className="flex justify-between items-start">
                        <span className="title">{t("label:expense_account")}</span>
                        <span className="text">{isExpenseAccountUniform ? expenseAccountLineItem?.accountName : t("label:splitted")}</span>
                    </li>
                ) : null}

                {/* Vendor */}
                <PurchaseOrderVendorField
                    purchaseOrder={purchaseOrder}
                    areInputsDisabled={PURCHASE_ORDER_STATUSES_UPDATE_FORBIDDEN.includes(purchaseOrder.state.currentState)}
                    isReadOnly={PURCHASE_ORDER_STATUSES_UPDATE_FORBIDDEN.includes(purchaseOrder.state.currentState)}
                />

                <CreditCardLink card={card} />
            </ul>
        </div>
    )
}
